import {
  type ClipboardEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Chip, Input, Select } from "@legal-500/legal500-design-system";
import type {
  FeatureFlagVariant,
  Rollout,
  TargetingOperator,
} from "../../../api/platform.service.js";
import {
  MAX_RULES,
  OPERATOR_LABELS,
  type RuleFieldErrors,
  type TargetingRuleDraft,
  type TargetingValidation,
  createRuleDraft,
  describeTargeting,
  hasSlotRule,
  mergeValues,
  moveRule,
  parseValueInput,
} from "./targeting";
import styles from "./FeatureFlags.module.css";

export const DISABLED_TARGETING_WARNING =
  "This flag is disabled, so targeting rules are ignored and every request gets the default variant.";

export const SLOT_TARGETING_WARNING =
  "Slot names change when a sync switches live traffic to a new slot. A rule that names the current live slot stops matching after the next switch, and live requests then get the 'Otherwise' variant. Update slot rules after each switch, or leave the flag enabled with no rules to apply it to every slot. In legacy mode requests have no slot, so 'is one of' slot rules never match and 'is not one of' rules always do.";

const ROLLOUT_OPTION_VALUE = "";

const OPERATOR_OPTIONS = (Object.keys(OPERATOR_LABELS) as TargetingOperator[]).map(operator => ({
  value: operator,
  label: OPERATOR_LABELS[operator],
}));

type TargetingEditorProps = {
  rules: TargetingRuleDraft[];
  fallbackVariantKey: string | null;
  onChange: (rules: TargetingRuleDraft[], fallbackVariantKey: string | null) => void;
  variants: FeatureFlagVariant[];
  /** The form's current Enabled value. */
  enabled: boolean;
  /** The rollout the form would save, used to explain the no-match case. */
  rollout: Rollout | null;
  disabled?: boolean;
  validation: TargetingValidation;
};

function variantOptions(variants: FeatureFlagVariant[], current: string | null) {
  const options = variants.map(v => ({ value: v.key, label: v.name || v.key }));
  // Keep a stored but undeclared key visible so the select doesn't silently show another option.
  if (current && !variants.some(v => v.key === current)) {
    options.push({ value: current, label: `${current} (not declared)` });
  }
  return options;
}

type RuleCardProps = {
  /** Stable per rule, so the editor can focus a rule's controls by id. */
  idPrefix: string;
  rule: TargetingRuleDraft;
  index: number;
  count: number;
  variants: FeatureFlagVariant[];
  errors: RuleFieldErrors;
  disabled: boolean;
  onUpdate: (rule: TargetingRuleDraft) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
};

function RuleCard({
  idPrefix,
  rule,
  index,
  count,
  variants,
  errors,
  disabled,
  onUpdate,
  onMove,
  onRemove,
}: RuleCardProps) {
  const number = index + 1;
  const [pending, setPending] = useState("");
  const [touched, setTouched] = useState({ attribute: false, values: false });
  const moveUpRef = useRef<HTMLButtonElement>(null);
  const moveDownRef = useRef<HTMLButtonElement>(null);
  const pendingMoveFocus = useRef<-1 | 1 | null>(null);

  // Refocus after a move: the browser can drop focus when React reorders the node, and the
  // button that was clicked is disabled once the rule reaches either end.
  useEffect(() => {
    const direction = pendingMoveFocus.current;
    if (direction === null) return;
    pendingMoveFocus.current = null;
    const atEnd = direction === -1 ? index === 0 : index === count - 1;
    const clicked = direction === -1 ? moveUpRef.current : moveDownRef.current;
    const other = direction === -1 ? moveDownRef.current : moveUpRef.current;
    (atEnd ? other : clicked)?.focus();
  }, [index, count]);

  const move = (direction: -1 | 1) => {
    pendingMoveFocus.current = direction;
    onMove(direction);
  };

  const commit = (text: string) => {
    const parsed = parseValueInput(text);
    if (parsed.length > 0) onUpdate({ ...rule, values: mergeValues(rule.values, parsed) });
  };

  const handleValueChange = (text: string) => {
    if (!text.includes(",")) {
      setPending(text);
      return;
    }
    // Everything before the last comma becomes chips; the rest stays in the input.
    const lastComma = text.lastIndexOf(",");
    commit(text.slice(0, lastComma));
    setPending(text.slice(lastComma + 1).trimStart());
  };

  const handleValueKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    commit(pending);
    setPending("");
  };

  const handleValuePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (!pasted.includes(",")) return;
    e.preventDefault();
    commit(`${pending},${pasted}`);
    setPending("");
  };

  const handleValueBlur = () => {
    commit(pending);
    setPending("");
    setTouched(prev => ({ ...prev, values: true }));
  };

  const removeValue = (valueIndex: number) => {
    onUpdate({ ...rule, values: rule.values.filter((_, i) => i !== valueIndex) });
    setTouched(prev => ({ ...prev, values: true }));
  };

  // Show errors once the field has been touched or has content, so a new rule isn't all red.
  const attributeError = touched.attribute || rule.attribute !== "" ? errors.attribute : undefined;
  const valuesError = touched.values || rule.values.length > 0 ? errors.values : undefined;

  const labelId = `${idPrefix}-label`;

  return (
    <li className={styles.targetingRule}>
      <div role="group" aria-labelledby={labelId}>
        <div className={styles.targetingRuleHeader}>
          <span id={labelId} className={styles.targetingRuleLabel}>
            Rule {number}
          </span>
          {!disabled && (
            <div className={styles.targetingRuleControls}>
              <Button
                ref={moveUpRef}
                id={`${idPrefix}-move-up`}
                variant="text"
                size="small"
                onClick={() => move(-1)}
                disabled={index === 0}
                aria-label={`Move rule ${number} up`}
              >
                Move up
              </Button>
              <Button
                ref={moveDownRef}
                id={`${idPrefix}-move-down`}
                variant="text"
                size="small"
                onClick={() => move(1)}
                disabled={index === count - 1}
                aria-label={`Move rule ${number} down`}
              >
                Move down
              </Button>
              <Button
                id={`${idPrefix}-remove`}
                variant="text"
                size="small"
                color="secondary"
                onClick={onRemove}
                aria-label={`Remove rule ${number}`}
              >
                Remove
              </Button>
            </div>
          )}
        </div>

        <div className={styles.targetingRuleFields}>
          <div className={styles.targetingField}>
            <Input
              id={`${idPrefix}-attribute`}
              label="Attribute"
              placeholder="e.g. slot"
              value={rule.attribute}
              onChange={e => onUpdate({ ...rule, attribute: e.target.value })}
              onBlur={() => {
                const trimmed = rule.attribute.trim();
                if (trimmed !== rule.attribute) onUpdate({ ...rule, attribute: trimmed });
                setTouched(prev => ({ ...prev, attribute: true }));
              }}
              disabled={disabled}
              error={attributeError}
              autoComplete="off"
              spellCheck={false}
              fullWidth
            />
          </div>
          <div className={styles.targetingField}>
            <Select
              id={`${idPrefix}-operator`}
              label="Operator"
              value={rule.operator}
              onChange={e => onUpdate({ ...rule, operator: e.target.value as TargetingOperator })}
              options={OPERATOR_OPTIONS}
              disabled={disabled}
              fullWidth
            />
          </div>
          <div className={styles.targetingField}>
            <Select
              id={`${idPrefix}-variant`}
              label="Serve variant"
              value={rule.variantKey}
              onChange={e => onUpdate({ ...rule, variantKey: e.target.value })}
              options={variantOptions(variants, rule.variantKey)}
              disabled={disabled}
              error={errors.variantKey}
              fullWidth
            />
          </div>
        </div>

        <div className={styles.targetingValues}>
          {disabled ? (
            <span className={styles.targetingValuesLabel}>Values</span>
          ) : (
            <label htmlFor={`${idPrefix}-values`} className={styles.targetingValuesLabel}>
              Values
            </label>
          )}
          {rule.values.length > 0 && (
            <ul className={styles.targetingChips} aria-label={`Rule ${number} values`}>
              {rule.values.map((value, valueIndex) => (
                <li key={`${valueIndex}-${value}`}>
                  <Chip size="small" variant="outlined" className={styles.targetingChip}>
                    <span className={styles.targetingChipText} title={value}>
                      {value}
                    </span>
                    {!disabled && (
                      <button
                        type="button"
                        className={styles.targetingChipRemove}
                        onClick={() => removeValue(valueIndex)}
                        aria-label={`Remove value ${value}`}
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    )}
                  </Chip>
                </li>
              ))}
            </ul>
          )}
          {!disabled && (
            <Input
              id={`${idPrefix}-values`}
              placeholder="Add a value"
              value={pending}
              onChange={e => handleValueChange(e.target.value)}
              onKeyDown={handleValueKeyDown}
              onPaste={handleValuePaste}
              onBlur={handleValueBlur}
              error={valuesError}
              helperText="Press Enter or type a comma to add a value."
              autoComplete="off"
              spellCheck={false}
              fullWidth
            />
          )}
        </div>
      </div>
    </li>
  );
}

type FocusRequest = { kind: "add"; ruleId: string } | { kind: "remove"; index: number };

export function TargetingEditor({
  rules,
  fallbackVariantKey,
  onChange,
  variants,
  enabled,
  rollout,
  disabled = false,
  validation,
}: TargetingEditorProps) {
  const baseId = useId();
  const variantKeys = useMemo(() => variants.map(v => v.key), [variants]);
  const hasRules = rules.length > 0;
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const focusRequest = useRef<FocusRequest | null>(null);
  const ruleIdPrefix = (ruleId: string) => `${baseId}-${ruleId}`;

  // Runs once the parent has applied an add or remove.
  useEffect(() => {
    const request = focusRequest.current;
    if (!request) return;
    focusRequest.current = null;
    if (request.kind === "add") {
      document.getElementById(`${baseId}-${request.ruleId}-attribute`)?.focus();
      return;
    }
    const next = rules[request.index] ?? rules[request.index - 1];
    if (next) document.getElementById(`${baseId}-${next.id}-remove`)?.focus();
    else addButtonRef.current?.focus();
  }, [rules, baseId]);

  const explanation = useMemo(
    () => (hasRules ? describeTargeting(rules, fallbackVariantKey, rollout, variantKeys) : []),
    [hasRules, rules, fallbackVariantKey, rollout, variantKeys],
  );

  const updateRule = (index: number, next: TargetingRuleDraft) => {
    onChange(
      rules.map((r, i) => (i === index ? next : r)),
      fallbackVariantKey,
    );
  };

  const removeRule = (index: number) => {
    const next = rules.filter((_, i) => i !== index);
    focusRequest.current = { kind: "remove", index };
    onChange(next, next.length === 0 ? null : fallbackVariantKey);
  };

  const addRule = () => {
    const rule = createRuleDraft(variantKeys);
    focusRequest.current = { kind: "add", ruleId: rule.id };
    onChange([...rules, rule], fallbackVariantKey);
  };

  const fallbackOptions = [
    { value: ROLLOUT_OPTION_VALUE, label: "Rollout / default (no override)" },
    ...variantOptions(variants, fallbackVariantKey),
  ];

  const titleId = `${baseId}-title`;

  return (
    <section className={styles.editSection} aria-labelledby={titleId}>
      <h4 id={titleId} className={styles.editSectionTitle}>
        Targeting
      </h4>
      <p className={styles.targetingDescription}>
        Serve a variant to requests whose attributes match a rule. Rules are checked in order and
        the first match wins.
      </p>

      {!enabled && hasRules && (
        <div className={styles.targetingWarning} role="status">
          {DISABLED_TARGETING_WARNING}
        </div>
      )}

      {variants.length === 0 && (
        <p className={styles.targetingNote}>
          Targeting needs declared variants, and this flag doesn't declare any.
        </p>
      )}

      {hasRules && (
        <ol className={styles.targetingRules}>
          {rules.map((rule, index) => (
            <RuleCard
              key={rule.id}
              idPrefix={ruleIdPrefix(rule.id)}
              rule={rule}
              index={index}
              count={rules.length}
              variants={variants}
              errors={validation.ruleErrors[index] ?? {}}
              disabled={disabled}
              onUpdate={next => updateRule(index, next)}
              onMove={delta => onChange(moveRule(rules, index, delta), fallbackVariantKey)}
              onRemove={() => removeRule(index)}
            />
          ))}
        </ol>
      )}

      {validation.formErrors.rules && (
        <p className={styles.targetingError} role="alert">
          {validation.formErrors.rules}
        </p>
      )}

      {!disabled && variants.length > 0 && (
        <div className={styles.targetingAddRow}>
          <Button
            ref={addButtonRef}
            variant="outlined"
            size="small"
            onClick={addRule}
            disabled={rules.length >= MAX_RULES}
          >
            Add rule
          </Button>
          {rules.length >= MAX_RULES && (
            <span className={styles.targetingHint}>A flag can have at most {MAX_RULES} rules.</span>
          )}
        </div>
      )}

      {hasRules && (
        <div className={styles.targetingFallback}>
          <Select
            id={`${baseId}-fallback`}
            label="Otherwise serve"
            value={fallbackVariantKey ?? ROLLOUT_OPTION_VALUE}
            onChange={e =>
              onChange(rules, e.target.value === ROLLOUT_OPTION_VALUE ? null : e.target.value)
            }
            options={fallbackOptions}
            disabled={disabled}
            error={validation.formErrors.fallback}
            helperText="Used when no rule matches."
            fullWidth
          />
        </div>
      )}

      {/* Mounted with the first rule so the warning is announced when "slot" is typed. */}
      {hasRules && (
        <div role="status">
          {hasSlotRule(rules) && (
            <div className={styles.targetingWarning}>{SLOT_TARGETING_WARNING}</div>
          )}
        </div>
      )}

      <div className={styles.targetingExplain} aria-live="polite">
        {explanation.length > 0 && (
          <>
            <span className={styles.infoLabel}>How requests are evaluated</span>
            <ul className={styles.targetingExplainList}>
              {explanation.map((line, i) => (
                // Lines can repeat while editing, so the index is part of the key.
                <li key={`${i}-${line}`}>{line}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}
